export const CUSTOMIZATION_KEY = 'customizationData';

export type CustomizationData = Record<string, any>;

type WebStorageDriver = 'session' | 'local';

let activeStorageDriver: WebStorageDriver = 'session';

type CustomizationDataListener = () => void;

const customizationDataListeners = new Set<CustomizationDataListener>();

/**
 * Subscribes to every write made through `setCustomizationData`/`updateCustomizationData`,
 * regardless of which field changed or which call site wrote it. Returns an unsubscribe function.
 */
export function subscribeToCustomizationData(listener: CustomizationDataListener): () => void {
  customizationDataListeners.add(listener);
  return () => customizationDataListeners.delete(listener);
}

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;

  try {
    return activeStorageDriver === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
};

const parseCustomizationData = (rawValue: string | null): CustomizationData => {
  if (!rawValue) return {};

  try {
    const parsed = JSON.parse(rawValue);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export function configureCustomizationStorage(driver: WebStorageDriver): void {
  activeStorageDriver = driver;
}

export function getCustomizationData<T extends CustomizationData = CustomizationData>(): T {
  const storage = getStorage();
  if (!storage) return {} as T;

  return parseCustomizationData(storage.getItem(CUSTOMIZATION_KEY)) as T;
}

export function setCustomizationData(data: CustomizationData): void {
  const storage = getStorage();
  if (!storage) return;

  storage.setItem(CUSTOMIZATION_KEY, JSON.stringify(data ?? {}));
  customizationDataListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // A subscriber's own bug must not break the write for this (or any other unrelated) caller.
    }
  });
}

export function updateCustomizationData(
  updater: (current: CustomizationData) => CustomizationData,
): CustomizationData {
  const current = getCustomizationData();
  const next = updater(current) ?? {};
  setCustomizationData(next);
  return next;
}

export function clearCustomizationData(): void {
  const storage = getStorage();
  if (!storage) return;

  storage.removeItem(CUSTOMIZATION_KEY);
}

/**
 * Remove any legacy localStorage entry for customizationData.
 * Call once at app startup so stale data doesn't persist across sessions.
 */
export function clearLegacyCustomizationData(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(CUSTOMIZATION_KEY);
  } catch {
    // Ignore storage access errors (e.g. Safari private mode)
  }
}
